import { useApi } from '@/hooks/useApi';
import {
  Wrench,
  Plus,
  Calendar,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Loader2,
} from 'lucide-react';

interface MaintenanceRecord {
  id: string;
  vehicleName: string;
  type: string;
  dueDate: string;
  status: 'scheduled' | 'upcoming' | 'overdue' | 'in_progress' | 'completed' | 'cancelled';
  vendor: string | null;
  cost: number | null;
}

interface MaintenanceData {
  records: MaintenanceRecord[];
  scheduled: number;
  overdue: number;
  completedThisMonth: number;
  avgCost: number;
}

const STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
  upcoming: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  overdue: 'bg-red-500/10 text-red-400 border-red-500/30',
  in_progress: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  completed: 'bg-zinc-500/5 text-zinc-500 border-zinc-600/30',
  cancelled: 'bg-zinc-500/5 text-zinc-600 border-zinc-700/30',
};

function isPast(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

export default function MaintenancePage() {
  const { data, isLoading } = useApi<MaintenanceData>('/api/maintenance');

  const records = data?.records ?? [];
  const scheduled = data?.scheduled ?? 0;
  const overdue = data?.overdue ?? 0;
  const completedThisMonth = data?.completedThisMonth ?? 0;
  const avgCost = data?.avgCost ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Wrench className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Maintenance</h1>
            <p className="text-sm text-zinc-400">Service schedules and work orders</p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" />
          Schedule Maintenance
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Calendar} label="Scheduled" value={scheduled} />
        <MetricCard icon={AlertTriangle} label="Overdue" value={overdue} warn />
        <MetricCard icon={CheckCircle} label="Completed This Month" value={completedThisMonth} accent />
        <MetricCard icon={DollarSign} label="Avg Cost" value={`$${avgCost.toLocaleString()}`} />
      </div>

      {/* Table */}
      <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No maintenance records</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Vehicle</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Due Date</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Vendor</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase tracking-wide px-4 py-3">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-zinc-100">{r.vehicleName}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-zinc-300">{r.type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${isPast(r.dueDate) && r.status !== 'completed' && r.status !== 'cancelled' ? 'text-red-400' : 'text-zinc-400'}`}>
                        {new Date(r.dueDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[r.status] ?? STATUS_STYLES.scheduled}`}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-zinc-400">{r.vendor ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-zinc-300 font-mono">
                        {r.cost != null ? `$${r.cost.toLocaleString()}` : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  accent,
  warn,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-zinc-500" />
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{label}</span>
      </div>
      <span className={`text-2xl font-bold ${accent ? 'text-emerald-400' : warn ? 'text-red-400' : 'text-zinc-100'}`}>
        {value}
      </span>
    </div>
  );
}
